import * as React from "react";
import {
  UitkLayoutGrid,
  UitkLayoutGridItem,
  UitkLayoutConditionalGridSpan,
  UitkLayoutConditionalGridColumnRow,
} from "uitk-react-layout-grid";
import { FromAirportViewProps } from "../../typings";
import { Location } from "src/components/shared/Location/Location";
import { Dates } from "src/components/shared/Dates/Dates";
import { TimeDropdown } from "src/components/shared/TimeDropdown/TimeDropdown";
import { BookRoundtripToggle } from "./BookRoundtripToggle";
import { BookRoundtrip } from "./BookRoundtrip";
import { Travelers } from "src/components/shared/Travelers/Travelers";
import { observer } from "mobx-react";
import { locKeys } from "../l10n";
import { GTHiddenSearchData } from "./GTHiddenSearchData";
import { Viewport, ViewSmall, ViewMedium } from "@shared-ui/viewport-context";
import { DirectionLabel } from "./DirectionLabel";
import { LobErrorSummary } from "src/components/shared/LobErrorSummary/LobErrorSummary";

export const FromAirportView: React.FunctionComponent<FromAirportViewProps> = observer((props) => {
  const {
    carWizardState,
    carWizardState: { pickupTime, returnTime, isFromAirportFormValid, updateInvalidFormsState },
    toggleBookRoundtrip,
    setInputsRefs,
  } = props;

  const { gtHeaderFrom, gtArrivalTime, gtHeaderTo } = locKeys;
  const format12hrConfig = carWizardState.config.timeFormats.timeFormat12Hour;

  const mainGridLayout: UitkLayoutConditionalGridColumnRow = { medium: 12 };
  const halfGridColumn: UitkLayoutConditionalGridSpan = { medium: 6 };

  const subheadingColSpan: UitkLayoutConditionalGridSpan = { small: 1, medium: 6 };

  const locationColSpan: UitkLayoutConditionalGridSpan = { small: 1, medium: 12 };

  const travelersColSpan: UitkLayoutConditionalGridSpan = { small: 1, medium: 12 };
  const datesColSpan: UitkLayoutConditionalGridSpan = { small: 1, medium: 6 };
  const timeDropdownColSpan: UitkLayoutConditionalGridSpan = { small: 1, medium: 6 };

  const roundTripDatesColSpan: UitkLayoutConditionalGridSpan = { small: 1, medium: 3 };
  const roundTripTimeColSpan: UitkLayoutConditionalGridSpan = { small: 1, medium: 3 };

  const errorSummaryGridColumns: UitkLayoutConditionalGridSpan = { small: 2, medium: 12 };

  if (isFromAirportFormValid) {
    updateInvalidFormsState();
  }

  return (
    <>
      {!isFromAirportFormValid && (
        <LobErrorSummary
          locHeadingToken={props.getLocError(false)!}
          locHeadingArgs={carWizardState.moreThanOneError() && [carWizardState.numberOfErrors]}
          locLinkToken={props.getLocError(true)}
          linkClickFocusId="id-roundtrip-error"
          inputErrorRef={carWizardState.errorInputRef}
          colSpan={errorSummaryGridColumns}
          setInputsRefs={setInputsRefs}
        />
      )}

      <Viewport>
        <ViewSmall>
          <>
            <DirectionLabel colSpan={subheadingColSpan} locKey={gtHeaderFrom} />
            <Location
              setInputsRefs={setInputsRefs}
              originField={true}
              destinationField={false}
              lobState={carWizardState}
              colSpan={locationColSpan}
            />
            <Dates lobState={carWizardState} singleDate={true} colSpan={datesColSpan} />
            <TimeDropdown
              labelKey={gtArrivalTime}
              timeValues={carWizardState.config.gtConfig.timeValues}
              onChange={carWizardState.setPickupTime}
              value={pickupTime}
              invalidKey={carWizardState.timeStartInvalidKey}
              colSpan={timeDropdownColSpan}
              use12hr={format12hrConfig}
            />

            <DirectionLabel colSpan={subheadingColSpan} locKey={gtHeaderTo} />

            <Location
              setInputsRefs={setInputsRefs}
              originField={false}
              destinationField={true}
              lobState={carWizardState}
              colSpan={locationColSpan}
            />
            <Travelers lobState={carWizardState} colSpan={travelersColSpan} />
          </>
        </ViewSmall>

        <ViewMedium>
          <>
            <UitkLayoutGridItem colSpan={halfGridColumn}>
              <UitkLayoutGrid columns={mainGridLayout} space="three">
                <DirectionLabel colSpan={subheadingColSpan} locKey={gtHeaderFrom} />
                <Location
                  setInputsRefs={setInputsRefs}
                  originField={true}
                  destinationField={false}
                  lobState={carWizardState}
                  colSpan={locationColSpan}
                />

                <Travelers lobState={carWizardState} colSpan={travelersColSpan} />
              </UitkLayoutGrid>
            </UitkLayoutGridItem>

            <UitkLayoutGridItem colSpan={halfGridColumn}>
              <UitkLayoutGrid columns={mainGridLayout} space="three">
                <DirectionLabel colSpan={subheadingColSpan} locKey={gtHeaderTo} />
                <Location
                  setInputsRefs={setInputsRefs}
                  originField={false}
                  destinationField={true}
                  lobState={carWizardState}
                  colSpan={locationColSpan}
                />
                <Dates lobState={carWizardState} singleDate={true} colSpan={datesColSpan} />
                <TimeDropdown
                  labelKey={gtArrivalTime}
                  timeValues={carWizardState.config.gtConfig.timeValues}
                  onChange={carWizardState.setPickupTime}
                  value={pickupTime}
                  invalidKey={carWizardState.timeStartInvalidKey}
                  colSpan={timeDropdownColSpan}
                  use12hr={format12hrConfig}
                />
              </UitkLayoutGrid>
            </UitkLayoutGridItem>
          </>
        </ViewMedium>
      </Viewport>
      <BookRoundtripToggle toggleBookRoundtrip={toggleBookRoundtrip} roundTripChecked={carWizardState.roundTrip} />
      {carWizardState.roundTrip && (
        <BookRoundtrip
          carWizardState={carWizardState}
          datesColSpan={roundTripDatesColSpan}
          timeColSpan={roundTripTimeColSpan}
        />
      )}
      <GTHiddenSearchData pickUpTime={pickupTime} dropOffTime={returnTime} />
    </>
  );
});
